"use client";

import React, { useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
            obs.unobserve(e.target);
          }
        }),
      { threshold: 0.07 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

interface PhaseItem {
  bullet: string;
  bold: string;
  text: string;
}

interface Phase {
  tag: string;
  title: string;
  items: PhaseItem[];
}

const phases: Phase[] = [
  {
    tag: 'PHASE I',
    title: 'Quantitative Extraction & Screening',
    items: [
      {
        bullet: 'Equities:',
        bold: 'Equities:',
        text: 'We screen thousands of global dividend-paying equities against strict parameters, including 10-year free cash flow stability, payout ratio sustainability, and exposure to high-growth sectors (such as AI infrastructure).',
      },
      {
        bullet: 'Forex:',
        bold: 'Forex:',
        text: 'We track interbank positioning, global liquidity metrics, and sovereign bond yield differentials to identify asymmetric currency setups.',
      },
      {
        bullet: 'Digital Assets:',
        bold: 'Digital Assets:',
        text: 'We deploy deep on-chain analytics, monitoring dormant whale wallet movements, institutional fund inflows, and network active node momentum.',
      },
    ],
  },
  {
    tag: 'PHASE II',
    title: 'Algorithmic Synthesis & Risk Modeling',
    items: [
      {
        bullet: 'The Yield Forecast Model:',
        bold: 'The Yield Forecast Model:',
        text: 'We project dividend sustainability by running Monte Carlo simulations against a company\'s balance sheet under various global recession scenarios.',
      },
      {
        bullet: 'Carry Trade Optimization:',
        bold: 'Carry Trade Optimization:',
        text: 'For FX markets, we analyze central bank rate divergence and swap rates to identify high-probability, positive-carry environments.',
      },
      {
        bullet: 'Narrative Strength Scoring:',
        bold: 'Narrative Strength Scoring:',
        text: 'For digital assets, we quantify social sentiment and developer activity to separate foundational blockchain infrastructure from speculative noise.',
      },
    ],
  },
  {
    tag: 'PHASE III',
    title: 'The Institutional Output',
    items: [
      {
        bullet: 'Real-Time Alerts:',
        bold: 'Real-Time Alerts:',
        text: 'Immediate notifications of structural market shifts, sudden dividend cuts, or major on-chain liquidity events.',
      },
      {
        bullet: 'Quarterly Global Outlooks:',
        bold: 'Quarterly Global Outlooks:',
        text: 'Deep-dive reports mapping out the macroeconomic trends that will dictate asset allocation for the next 90 to 180 days.',
      },
      {
        bullet: 'Actionable Catalysts:',
        bold: 'Actionable Catalysts:',
        text: 'A forward-looking calendar of earnings dates, central bank decisions, and token unlocks that will drive volatility and opportunity.',
      },
    ],
  },
];

const phaseVisuals = [
  {
    icon: (
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
        <rect width="56" height="56" rx="14" fill="rgba(163,212,255,0.08)" />
        <path d="M14 40L22 24L30 32L38 18" stroke="#A3D4FF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="38" cy="18" r="3" fill="#A3D4FF"/>
        <path d="M14 44h28" stroke="rgba(163,212,255,0.3)" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="22" cy="24" r="2" fill="rgba(163,212,255,0.5)"/>
        <circle cx="30" cy="32" r="2" fill="rgba(163,212,255,0.5)"/>
      </svg>
    ),
    label: 'Global Data Aggregation',
    sublabel: 'Millions of raw data points synthesized in real-time',
    accent: '#A3D4FF',
  },
  {
    icon: (
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
        <rect width="56" height="56" rx="14" fill="rgba(163,212,255,0.08)" />
        <rect x="14" y="28" width="6" height="14" rx="2" fill="rgba(163,212,255,0.4)"/>
        <rect x="25" y="20" width="6" height="22" rx="2" fill="rgba(163,212,255,0.65)"/>
        <rect x="36" y="14" width="6" height="28" rx="2" fill="#A3D4FF"/>
        <path d="M12 40h32" stroke="rgba(163,212,255,0.3)" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    label: 'Monte Carlo Stress Testing',
    sublabel: 'Algorithmic models forecasting forward-looking performance',
    accent: '#4A90E2',
  },
  {
    icon: (
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
        <rect width="56" height="56" rx="14" fill="rgba(163,212,255,0.08)" />
        <circle cx="28" cy="28" r="12" stroke="#A3D4FF" strokeWidth="2" strokeDasharray="4 3"/>
        <circle cx="28" cy="28" r="5" fill="rgba(163,212,255,0.2)" stroke="#A3D4FF" strokeWidth="1.5"/>
        <circle cx="28" cy="28" r="2" fill="#A3D4FF"/>
        <path d="M28 16v-4M28 44v-4M16 28h-4M44 28h-4" stroke="rgba(163,212,255,0.5)" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    label: 'Institutional-Grade Output',
    sublabel: 'Actionable intelligence delivered with precision timing',
    accent: '#63B3ED',
  },
];

export default function MethodologyPage() {
  useReveal();

  return (
    <div style={{ background: '#0A2540', minHeight: '100vh', fontFamily: 'var(--font-body)' }}>
      <Header />

      {/* ── Hero ── */}
      <section
        className="relative flex flex-col items-center justify-center text-center overflow-hidden"
        style={{ paddingTop: '140px', paddingBottom: '100px', minHeight: '60vh' }}
      >
        {/* Background grid */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(163,212,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(163,212,255,0.04) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
        {/* Radial glow */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: '10%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '700px',
            height: '400px',
            background: 'radial-gradient(ellipse at center, rgba(74,144,226,0.18) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
        />

        <div className="relative z-10 px-6" style={{ maxWidth: '860px', margin: '0 auto' }}>
          {/* Eyebrow */}
          <div
            className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full"
            style={{ background: 'rgba(163,212,255,0.1)', border: '1px solid rgba(163,212,255,0.25)' }}
          >
            <span
              className="text-xs font-mono tracking-widest uppercase"
              style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.18em' }}
            >
              Research Architecture
            </span>
          </div>

          <h1
            className="font-display font-bold leading-tight mb-6"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(2.4rem, 5.5vw, 4rem)',
              color: '#FFFFFF',
              letterSpacing: '-0.02em',
            }}
          >
            The DividendChase Methodology.
          </h1>

          <p
            className="text-lg leading-relaxed"
            style={{
              color: '#E3F2FD',
              maxWidth: '620px',
              margin: '0 auto',
              fontSize: 'clamp(1rem, 2vw, 1.2rem)',
              opacity: 0.85,
            }}
          >
            Engineering Alpha through Algorithmic Rigor and Institutional Independence.
          </p>
        </div>
      </section>

      {/* ── Ethos Section ── */}
      <section className="reveal px-6 pb-20" style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div
          className="rounded-2xl p-10 md:p-14"
          style={{
            background: '#1A2A44',
            border: '1px solid #0F4C81',
          }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div
              className="w-1 rounded-full"
              style={{ height: '40px', background: 'linear-gradient(180deg, #A3D4FF 0%, #4A90E2 100%)' }}
            />
            <h2
              className="font-display font-bold"
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                color: '#FFFFFF',
                letterSpacing: '-0.01em',
              }}
            >
              The DividendChase Ethos
            </h2>
          </div>

          <div className="space-y-5" style={{ color: '#E3F2FD', lineHeight: '1.8', opacity: 0.85 }}>
            <p>
              At DividendChase LTD, we recognize that the modern financial landscape is saturated with retail noise,
              retail-focused media, and lagging indicators. Our methodology was engineered to cut through this static.
              We provide High-Net-Worth Individuals (HNWIs), Family Offices, and Institutional Investors with the same
              caliber of quantitative screening, macroeconomic synthesis, and predictive modeling utilized by top-tier
              hedge funds.
            </p>
            <p>
              Our approach relies on pure, unbiased intelligence. We have no paid promotions, no corporate sponsors,
              and zero conflicts of interest. Our sole mandate is capital preservation and aggressive yield generation
              for our subscribers.
            </p>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap gap-4 mt-8">
            {['No Paid Promotions', 'Zero Conflicts of Interest', 'No Corporate Sponsors'].map((badge) => (
              <div
                key={badge}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
                style={{
                  background: 'rgba(163,212,255,0.08)',
                  border: '1px solid rgba(163,212,255,0.2)',
                  color: '#A3D4FF',
                  fontFamily: 'var(--font-body)',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2.5 7L5.5 10L11.5 4" stroke="#A3D4FF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {badge}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Architecture Header ── */}
      <section className="reveal px-6 pb-10 text-center" style={{ maxWidth: '900px', margin: '0 auto' }}>
        <p
          className="text-xs font-mono tracking-widest uppercase mb-3"
          style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.2em' }}
        >
          Proprietary Research Architecture
        </p>
        <h2
          className="font-display font-bold"
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)',
            color: '#FFFFFF',
            letterSpacing: '-0.015em',
          }}
        >
          Our Multi-Asset Intelligence is Generated Through a Strict,{' '}
          <span style={{ color: '#A3D4FF' }}>Three-Phase Analytical Architecture</span>
        </h2>
      </section>

      {/* ── Three Phases — Alternating Layout ── */}
      <section className="px-6 pb-20" style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div className="space-y-10">
          {phases.map((phase, idx) => {
            const isEven = idx % 2 === 0;
            const visual = phaseVisuals[idx];
            return (
              <div
                key={phase.tag}
                className={`reveal rounded-2xl overflow-hidden flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                style={{
                  background: '#1A2A44',
                  border: '1px solid #0F4C81',
                  minHeight: '320px',
                }}
              >
                {/* Visual Panel */}
                <div
                  className="flex flex-col items-center justify-center p-10 md:p-14 flex-shrink-0"
                  style={{
                    width: '100%',
                    maxWidth: '340px',
                    background: isEven
                      ? 'linear-gradient(135deg, rgba(15,76,129,0.6) 0%, rgba(10,37,64,0.8) 100%)'
                      : 'linear-gradient(135deg, rgba(10,37,64,0.8) 0%, rgba(15,76,129,0.6) 100%)',
                    borderRight: isEven ? '1px solid rgba(15,76,129,0.5)' : 'none',
                    borderLeft: !isEven ? '1px solid rgba(15,76,129,0.5)' : 'none',
                  }}
                >
                  <div className="mb-5">{visual.icon}</div>
                  <p
                    className="text-center font-semibold text-sm mb-1"
                    style={{ color: '#FFFFFF', fontFamily: 'var(--font-body)' }}
                  >
                    {visual.label}
                  </p>
                  <p
                    className="text-center text-xs leading-relaxed"
                    style={{ color: '#E3F2FD', opacity: 0.6, fontFamily: 'var(--font-body)' }}
                  >
                    {visual.sublabel}
                  </p>
                </div>

                {/* Content Panel */}
                <div className="flex-1 p-8 md:p-12">
                  <div className="flex items-center gap-3 mb-2">
                    <span
                      className="text-xs font-mono tracking-widest uppercase px-3 py-1 rounded-full"
                      style={{
                        color: '#A3D4FF',
                        background: 'rgba(163,212,255,0.1)',
                        border: '1px solid rgba(163,212,255,0.2)',
                        fontFamily: 'var(--font-mono)',
                        letterSpacing: '0.18em',
                      }}
                    >
                      {phase.tag}
                    </span>
                  </div>

                  <h3
                    className="font-display font-bold mb-6"
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: 'clamp(1.2rem, 2.5vw, 1.6rem)',
                      color: '#FFFFFF',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {phase.title}
                  </h3>

                  <ul className="space-y-4">
                    {phase.items.map((item) => (
                      <li key={item.bullet} className="flex gap-3">
                        <div
                          className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full"
                          style={{ background: '#A3D4FF' }}
                        />
                        <p style={{ color: '#E3F2FD', lineHeight: '1.75', opacity: 0.85, fontSize: '0.95rem' }}>
                          <strong style={{ color: '#FFFFFF', fontWeight: 600 }}>{item.bold}</strong>{' '}
                          {item.text}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Closing CTA ── */}
      <section className="reveal px-6 pb-28" style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div
          className="rounded-2xl p-10 md:p-16 text-center relative overflow-hidden"
          style={{
            background: '#1A2A44',
            border: '1px solid #0F4C81',
          }}
        >
          {/* Glow */}
          <div
            className="absolute pointer-events-none"
            style={{
              top: '-60px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '500px',
              height: '300px',
              background: 'radial-gradient(ellipse at center, rgba(74,144,226,0.15) 0%, transparent 70%)',
              filter: 'blur(30px)',
            }}
          />

          <div className="relative z-10">
            <p
              className="text-xs font-mono tracking-widest uppercase mb-4"
              style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.2em' }}
            >
              Precision · Performance · Independence
            </p>

            <h2
              className="font-display font-bold mb-5"
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
                color: '#FFFFFF',
                letterSpacing: '-0.02em',
                lineHeight: '1.2',
              }}
            >
              We do not chase the market;{' '}
              <span style={{ color: '#A3D4FF' }}>we model its next move.</span>
            </h2>

            <p
              className="mb-10 mx-auto"
              style={{
                color: '#E3F2FD',
                opacity: 0.8,
                maxWidth: '580px',
                lineHeight: '1.75',
                fontSize: '1.05rem',
              }}
            >
              The DividendChase terminal equips you with the architecture required to build a resilient, high-yield
              portfolio across equities, fiat, and digital assets.
            </p>

            <Link
              href="/homepage#pricing"
              className="inline-flex items-center gap-2 font-bold px-8 py-4 rounded-full transition-all duration-200"
              style={{
                background: '#A3D4FF',
                color: '#0A2540',
                fontFamily: 'var(--font-body)',
                fontSize: '1rem',
                letterSpacing: '0.03em',
                border: '2px solid #A3D4FF',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
                (e.currentTarget as HTMLAnchorElement).style.color = '#A3D4FF';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = '#A3D4FF';
                (e.currentTarget as HTMLAnchorElement).style.color = '#0A2540';
              }}
            >
              Unlock the Terminal
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
